import { TextButton } from '@creditornot/cb-button';
import React, { useCallback } from 'react';
import toast from 'react-hot-toast';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { usePluralization } from 'common/hooks';

import FailedImagesList from './FailedImagesList';
import { MultiImageFileError } from './types';

type Props = {
  fileDropResult: 'success' | 'partial-success';
  validFiles: File[];
  fileErrors: MultiImageFileError[];
  onUndo: () => Promise<void>;
};

const ToastContentRoot = styled.div`
  display: flex;
  flex-direction: column;
`;

const UndoButtonContainer = styled.div`
  display: flex;
  width: 100%;
  justify-content: flex-end;
`;

const ToastContentWithUndoButton = ({ children, onUndo }: { children: React.ReactNode; onUndo: () => void }) => {
  return (
    <ToastContentRoot>
      {children}
      <UndoButtonContainer>
        <TextButton variant="grey" onClick={onUndo}>
          <FormattedMessage id="state.undo" />
        </TextButton>
      </UndoButtonContainer>
    </ToastContentRoot>
  );
};

export const useUploadToast = () => {
  const { FormattedPluralMessage } = usePluralization();

  const showUploadToast = useCallback(
    ({ fileDropResult, validFiles, fileErrors, onUndo }: Props) => {
      const toastId = String(Math.random());

      const handleUndoClick = () => {
        toast.remove(toastId);
        toast.promise(onUndo(), {
          loading: <FormattedMessage id="image-upload.listHeaderUpload.undo.undoChangesSubmitting" />,
          success: <FormattedMessage id="image-upload.listHeaderUpload.undo.undoSuccessDescription" />,
          error: <FormattedMessage id="image-upload.listHeaderUpload.undo.undoErrorDescription" />,
        });
      };

      if (fileDropResult === 'success') {
        toast.success(
          <ToastContentWithUndoButton onUndo={handleUndoClick}>
            <FormattedPluralMessage id="image-upload.success" count={validFiles.length} />
          </ToastContentWithUndoButton>,
          {
            duration: 10000,
            id: toastId,
          },
        );
      } else {
        toast(
          <ToastContentWithUndoButton onUndo={handleUndoClick}>
            <FormattedMessage
              id="image-upload.listHeaderUpload.errors.partial-success"
              values={{ successCount: validFiles.length, errorCount: fileErrors.length }}
            />
            <FailedImagesList errors={fileErrors} />
          </ToastContentWithUndoButton>,
          {
            duration: 20000,
            id: toastId,
          },
        );
      }
    },
    [FormattedPluralMessage],
  );

  return { showUploadToast };
};
